import Dexie, { type Table } from 'dexie'

import type { Activity, FieldDefsByMesgNum, FitFieldDef, Operation } from '@fit-editor/fit-core'

/**
 * Local IndexedDB persistence (M7): so a user's loaded activities, and
 * their edit history, survive closing/reopening the tab. Nothing here
 * ever leaves the device -- this app has zero backend, and this module
 * only talks to the browser's own IndexedDB.
 *
 * Deliberately a thin wrapper around Dexie rather than a "projects"
 * system: one row per loaded activity, keyed by the same stable id
 * `activity-store.ts` already assigns, storing exactly enough to
 * reconstruct an `EditHistoryState` (`original`/`log`/`cursor` -- see
 * `edit/history.ts`) plus the one other piece of per-activity state the
 * store needs to re-export to FIT (`fieldDefsByMesgNum`, serialized as
 * entries rather than a raw `Map` -- see `fieldDefsMapToEntries`'s doc
 * comment for why).
 *
 * Every exported function no-ops (resolves immediately, returning an
 * empty/`null` result where relevant) when `isPersistenceAvailable()` is
 * false, rather than throwing -- this keeps every call site in
 * `activity-store.ts` unconditional. That matters in two real
 * situations, not just hypothetically: jsdom (used by this project's
 * component tests) has no IndexedDB implementation at all, and some
 * browsers' private-browsing modes disable or heavily restrict it. Either
 * way, persistence silently degrades to "this session only" rather than
 * crashing the app -- the in-memory editing experience must never depend
 * on IndexedDB actually working.
 */

/** A `FieldDefsByMesgNum` Map, serialized as entries. */
export type FieldDefsEntry = [number, FitFieldDef[]]

export interface PersistedActivityRecord {
  id: string
  fileName: string
  importedAt: number
  original: Activity
  log: Operation[]
  cursor: number
  fieldDefsEntries: FieldDefsEntry[]
}

interface PersistedMetaRow {
  key: string
  value: string
}

const SELECTED_ACTIVITY_ID_KEY = 'selectedActivityId'

/**
 * `Map` is natively structured-clone-able in every evergreen browser
 * (and so, in principle, IndexedDB-storable directly), but converting to
 * a plain array of entries here avoids depending on that for a value
 * this app actually needs to round-trip correctly, and keeps the stored
 * row plain-data/inspectable (e.g. in DevTools' IndexedDB viewer) rather
 * than opaque.
 */
export function fieldDefsMapToEntries(map: FieldDefsByMesgNum): FieldDefsEntry[] {
  return Array.from(map.entries())
}

export function fieldDefsEntriesToMap(entries: FieldDefsEntry[]): FieldDefsByMesgNum {
  return new Map(entries)
}

/** False under jsdom (no IndexedDB at all) and some private-browsing modes. */
export function isPersistenceAvailable(): boolean {
  return typeof indexedDB !== 'undefined'
}

class FitEditorDatabase extends Dexie {
  activities!: Table<PersistedActivityRecord, string>
  meta!: Table<PersistedMetaRow, string>

  constructor() {
    super('fit-editor')
    this.version(1).stores({
      activities: 'id, importedAt',
      meta: 'key',
    })
  }
}

let dbInstance: FitEditorDatabase | undefined

function getDb(): FitEditorDatabase {
  dbInstance ??= new FitEditorDatabase()
  return dbInstance
}

/** Upserts one activity's full persisted state. */
export async function saveActivityRecord(record: PersistedActivityRecord): Promise<void> {
  if (!isPersistenceAvailable()) return
  await getDb().activities.put(record)
}

export async function deleteActivityRecord(id: string): Promise<void> {
  if (!isPersistenceAvailable()) return
  await getDb().activities.delete(id)
}

export async function loadAllActivityRecords(): Promise<PersistedActivityRecord[]> {
  if (!isPersistenceAvailable()) return []
  return getDb().activities.toArray()
}

export async function saveSelectedActivityId(id: string | null): Promise<void> {
  if (!isPersistenceAvailable()) return
  if (id == null) {
    await getDb().meta.delete(SELECTED_ACTIVITY_ID_KEY)
  } else {
    await getDb().meta.put({ key: SELECTED_ACTIVITY_ID_KEY, value: id })
  }
}

export async function loadSelectedActivityId(): Promise<string | null> {
  if (!isPersistenceAvailable()) return null
  const row = await getDb().meta.get(SELECTED_ACTIVITY_ID_KEY)
  return row?.value ?? null
}

/** Removes everything this app has persisted. Not currently wired to any UI (out of this milestone's scope -- see the M7 handoff report), kept for completeness/future use and so it's independently testable. */
export async function clearAllPersistedActivities(): Promise<void> {
  if (!isPersistenceAvailable()) return
  await Promise.all([getDb().activities.clear(), getDb().meta.clear()])
}
